import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRightIcon, ChevronDownIcon, CircleAlertIcon, GlobeIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import Loading from '@/components/shared/Loading';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { getEnvConfig } from '@/lib/env-config';

const REGIONS = [
  { id: 'us', label: 'US', url: 'https://app.us.crimson.law/auth/login' },
  { id: 'uk-eu', label: 'UK / EU', url: 'https://app.crimson.law/auth/login' },
] as const;

type RegionId = typeof REGIONS[number]['id'];

function RegionSelector() {
  const [open, setOpen] = useState(false);
  const { environment } = getEnvConfig();
  const currentId: RegionId = environment === 'productionus' ? 'us' : 'uk-eu';
  const isProd = environment === 'productionus' || environment === 'productionuk';
  const current = REGIONS.find(r => r.id === currentId) ?? REGIONS[0];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-500 transition-colors"
      >
        <GlobeIcon className="h-4 w-4" />
        <span>{current.label}</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute bottom-full right-0 mb-1.5 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-max z-10"
          >
            {REGIONS.map(region =>
              region.id === currentId ? (
                <span key={region.id} role="menuitem" aria-disabled className="flex items-center px-3 py-2 text-sm text-crimson-600 font-medium cursor-default">
                  {region.label}
                </span>
              ) : isProd ? (
                <a
                  key={region.id}
                  role="menuitem"
                  href={region.url}
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  {region.label}
                </a>
              ) : (
                <span
                  key={region.id}
                  role="menuitem"
                  aria-disabled
                  className="flex items-center px-3 py-2 text-sm text-gray-400 cursor-default"
                >
                  {region.label}
                </span>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}

const Login = () => {
  const navigate = useNavigate();
  const { error } = useSearch({ from: '/auth/login' });
  const {
    user,
    isLoading,
    login,
    logout,
    error: errorMessage,
    setError: setAuthError,
    clearError: clearAuthError,
  } = useAuth();

  useEffect(() => {
    if (error) {
      setAuthError(error);
    }
  }, [error, setAuthError]);

  useEffect(() => {
    if (user && !errorMessage) {
      navigate({ to: '/home' });
    }
  }, [user, errorMessage, navigate]);

  if ((user && !errorMessage) || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loading size="lg" />
      </div>
    );
  }

  const handleLoginRedirect = () => {
    clearAuthError();

    if (user) {
      void logout().catch(() => undefined);
      return;
    }

    try {
      login('/home');
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Authentication failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-1.5 lg:p-2">
      <div className="flex min-h-[calc(100vh-0.75rem)] lg:min-h-[calc(100vh-1rem)] overflow-hidden rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.15)]">
        {/* Left panel - hero image (hidden on mobile) */}
        <div className="relative hidden lg:flex lg:w-1/2">
          <img src="/login-hero.jpg" alt="Courthouse columns" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
          <div className="relative z-10 flex flex-col justify-between p-12 h-full">
            <div>
              <img src="/logo-text-white.svg" alt="Crimson" width={160} height={44} className="h-9 w-auto" />
            </div>
            <div>
              <p className="text-lg text-white/80">The Case Intelligence Platform.</p>
              <div className="mt-4 h-px w-16 bg-white/30" />
            </div>
          </div>
        </div>

        {/* Right panel - login form */}
        <div className="relative flex w-full flex-col items-center justify-center bg-white px-6 py-12 lg:w-1/2">
          <div className="absolute top-0 left-0 right-0 h-1 bg-crimson-600 lg:hidden" />
          <div className="w-full max-w-md">
            <div className="flex flex-col items-center w-full space-y-6">
              {/* Branding */}
              <div className="text-center mb-2">
                <img
                  src="/logo-icon.svg"
                  alt="Crimson"
                  width={44}
                  height={44}
                  className="h-11 w-auto mx-auto"
                />
                <h2 className="mt-4 text-2xl font-semibold text-gray-900 tracking-tight">
                  Sign in to Crimson
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Access your litigation workspace
                </p>
              </div>

              {/* Error display */}
              {errorMessage && (
                <div className="w-full rounded-lg border-l-4 border-red-500 bg-red-50 p-4 shadow-xs">
                  <div className="flex items-start">
                    <div className="shrink-0">
                      <CircleAlertIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                      <div className="mt-1 text-sm text-red-700">{errorMessage}</div>
                      <div className="mt-3">
                        <a
                          href="mailto:support@crimson.law"
                          className="text-sm font-medium text-red-800 hover:text-red-600 underline"
                        >
                          Contact Support
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Login button */}
              <button
                onClick={handleLoginRedirect}
                className="group w-full bg-crimson-600/90 hover:bg-crimson-600 text-white text-xs font-medium py-2.5 px-5 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 tracking-wide"
              >
                <svg
                  className="h-3.5 w-3.5 opacity-40"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="1" y="1" width="9" height="9" fill="currentColor" opacity="0.9" />
                  <rect x="11" y="1" width="9" height="9" fill="currentColor" opacity="0.7" />
                  <rect x="1" y="11" width="9" height="9" fill="currentColor" opacity="0.6" />
                  <rect x="11" y="11" width="9" height="9" fill="currentColor" opacity="0.8" />
                </svg>
                <span>Sign in with Microsoft SSO</span>
                <svg
                  className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0 transition-all duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </button>

              {/* Footer */}
              <div className="text-center text-xs text-gray-400 w-full pt-2">
                <p>
                  By signing in, you agree to Crimson&apos;s{' '}
                  <a
                    href="https://www.crimson.law/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-crimson-600 hover:underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.crimson.law/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-crimson-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </p>
                <p className="mt-3">
                  &copy; {new Date().getFullYear()} Crimson LegalTech Inc.
                </p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-4 right-6">
            <RegionSelector />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
